import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DashboardLayout from '../components/DashboardLayout';
import DraggableTile from '../components/DraggableTile';
import PreviewModal from '../components/PreviewModal';
import IconPicker from '../components/IconPicker';
import IconLibraryPicker from '../components/IconLibraryPicker';
import { LibraryIcon, isLibraryIcon } from '../lib/iconLibrary';
import ImageLibrary from '../components/ImageLibrary';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import { api, getImageUrl, getToken } from '../utils/api';
import { useConfirm } from '../components/ConfirmProvider';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import { Plus, Eye, Settings, LayoutGrid, Send, Newspaper, Save, Image as ImageIcon, Share2, ExternalLink, Copy, Edit, Trash2, Copy as CopyIcon, ChevronUp, ChevronDown, Calendar, X, Database, FolderOpen, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToPushNotifications, isPushNotificationSupported } from '../utils/pushNotifications';


// Refactored Admin Components
import { 
  ColoursAndFontsCard, 
  HeaderSettingsCard, 
  AccessControlCard,
  WondeIntegration,
  NexusCmsIntegration
} from '../components/admin';
import { SocialMediaCard } from '../components/admin';
import { TileGridSettingsCard } from '../components/admin';

// Notification History Component
function NotificationHistory({ customer, users, groups, refreshKey }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, [refreshKey]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await api.getNotifications();
      // Get all notifications (admins can see all they sent)
      setNotifications(data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown User';
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'Unknown Group';
  };

  const handleStopRecurring = async (notifId) => {
    try {
      await api.stopRecurringNotification(notifId);
      toast.success('Recurring notification stopped');
      fetchNotifications();
    } catch (error) {
      console.error('Failed to stop recurring notification:', error);
      toast.error('Failed to stop notification');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
  }

  if (notifications.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No notifications sent yet</div>;
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {notifications.map((notif) => (
        <div key={notif.id} className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-sm">{notif.title}</h4>
                {notif.status === 'scheduled' && (
                  <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    Scheduled
                  </span>
                )}
                {notif.status === 'sent' && (
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    Sent
                  </span>
                )}
                {notif.status === 'recurring' && (
                  <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    🔄 Recurring ({notif.recurrence_type})
                  </span>
                )}
                {notif.status === 'stopped' && (
                  <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    Stopped
                  </span>
                )}
                {notif.status === 'completed' && (
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    Completed
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{notif.message}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            {notif.status === 'scheduled' && (
              <span className="text-muted-foreground font-medium">
                Scheduled for {new Date(notif.scheduled_for).toLocaleString()}
              </span>
            )}
            {notif.status === 'sent' && (
              <span className="text-muted-foreground">
                Sent {formatDistanceToNow(new Date(notif.sent_at || notif.created_at), { addSuffix: true })}
              </span>
            )}
            {notif.status === 'recurring' && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-purple-600 font-medium">
                  Next: {notif.next_send_at ? new Date(notif.next_send_at).toLocaleString() : 'N/A'}
                </span>
                <span className="text-muted-foreground">
                  Sent {notif.send_count || 0} time(s)
                </span>
                {notif.recurrence_end_date && (
                  <span className="text-muted-foreground">
                    Until: {new Date(notif.recurrence_end_date).toLocaleDateString()}
                  </span>
                )}
                <button
                  onClick={() => handleStopRecurring(notif.id)}
                  className="ml-auto px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  Stop
                </button>
              </div>
            )}
            {(notif.status === 'stopped' || notif.status === 'completed') && (
              <span className="text-muted-foreground">
                Sent {notif.send_count || 0} time(s) - {notif.status}
              </span>
            )}
            {notif.recipient_ids && notif.recipient_ids.length > 0 && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">
                {notif.recipient_ids.length} user(s): {notif.recipient_ids.slice(0, 2).map(id => getUserName(id)).join(', ')}
                {notif.recipient_ids.length > 2 && ` +${notif.recipient_ids.length - 2} more`}
              </span>
            )}
            {notif.recipient_groups && notif.recipient_groups.length > 0 && (
              <span className="bg-green-50 text-green-700 px-2 py-1 rounded">
                {notif.recipient_groups.length} group(s): {notif.recipient_groups.slice(0, 2).map(id => getGroupName(id)).join(', ')}
                {notif.recipient_groups.length > 2 && ` +${notif.recipient_groups.length - 2} more`}
              </span>
            )}
            {(!notif.recipient_ids || notif.recipient_ids.length === 0) && 
             (!notif.recipient_groups || notif.recipient_groups.length === 0) && (
              <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded">
                All Users
              </span>
            )}
            <span className="bg-gray-50 text-gray-700 px-2 py-1 rounded">
              {notif.read_by?.length || 0} read
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboardMobile() {
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('design');
  const [tiles, setTiles] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [articles, setArticles] = useState([]);
  const [tileDialogOpen, setTileDialogOpen] = useState(false);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [editingTile, setEditingTile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sharePWAOpen, setSharePWAOpen] = useState(false);
  const [gridCols, setGridCols] = useState(3);
  const [tileStyle, setTileStyle] = useState('soft');
  const [tileShape, setTileShape] = useState('default');
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [notificationRefreshKey, setNotificationRefreshKey] = useState(0);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { user } = useAuth();
  const tileIconInputRef = useRef(null);

  const [tileForm, setTileForm] = useState({
    title: '',
    url: '',
    icon: '',
    color: null,
    text_color: null,
    icon_color: null,
    border_color: null,
    visible_to_groups: [],
    tileType: 'link', // 'link' | 'phone' | 'chat'
    embed_code: ''
  });

  const [designForm, setDesignForm] = useState({
    header_text: '',
    show_header_text: true,
    header_text_color: '#2563EB',
    header_background_color: '#2563EB',
    header_text_size: 'xl',
    header_text_font: 'Inter',
    header_text_align: 'center',
    header_image_url: '',
    pwa_icon_url: '',
    portal_domain: '',  // Custom domain for portal URLs (e.g., https://taptile.app)
    tiles_section_title: 'Your Apps & Links',
    show_tiles_section_title: true,
    tiles_section_title_color: '#1E293B',
    social_section_title: 'Connect With Us',
    show_social_section_title: true,
    social_section_title_color: '#1E293B',
    primary_color: '#2563EB',
    background_color: '#F8FAFC',
    tile_color: '#FFFFFF',
    social_background_color: '#FFFFFF',
    font_color: '#1E293B',
    font_family: 'Inter',
    tile_size: 'default',
    tile_font_color: '#1E293B',
    tile_font_size: 'base',
    tile_style: 'soft',
    tile_shape: 'default',
    show_tile_title: true,
    show_tile_icon: true,
    grid_cols: 3,
    social_links_position: 'top',
    social_links_enabled: true,
    news_enabled: true,
    news_article_count: 3,
    require_authentication: true,
    public_tiles_enabled: false,
    show_inbox_button: true,
    show_welcome_message: true,
    button_color: '#2563EB',
    notification_mode: 'logged_in',  // 'logged_in' or 'all_devices'
  });

  const [socialLinksForm, setSocialLinksForm] = useState({
    facebook: '',
    facebook_enabled: false,
    twitter: '',
    twitter_enabled: false,
    linkedin: '',
    linkedin_enabled: false,
    instagram: '',
    instagram_enabled: false,
    youtube: '',
    youtube_enabled: false,
    tiktok: '',
    tiktok_enabled: false,
    bluesky: '',
    bluesky_enabled: false,
    custom: '',
    custom_enabled: false,
    custom_label: ''
  });

  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    recipient_ids: [],
    recipient_groups: [],
    send_immediately: true,
    scheduled_for: null,
    is_recurring: false,
    recurrence_type: null,
    recurrence_end_date: null
  });

  const [articleForm, setArticleForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    image_url: ''
  });

  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    member_ids: []
  });

  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    groups: []
  });

  // ── Tile form draft: auto-save to localStorage so an iframe refresh
  //    doesn't wipe a half-completed tile form ──────────────────────────
  const TILE_DRAFT_KEY = user?.customer_id
    ? `taptile_tile_draft_${user.customer_id}`
    : 'taptile_tile_draft';

  // Save draft whenever the dialog is open and form data changes
  useEffect(() => {
    if (!tileDialogOpen) return;
    const draft = { tileForm, editingTile, savedAt: new Date().toISOString() };
    localStorage.setItem(TILE_DRAFT_KEY, JSON.stringify(draft));
  }, [tileForm, tileDialogOpen]); // eslint-disable-line

  // On first load, check for a saved draft and restore it
  useEffect(() => {
    try {
      const raw = localStorage.getItem(TILE_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft?.tileForm?.title) return; // ignore empty drafts
      setTileForm(draft.tileForm);
      if (draft.editingTile) setEditingTile(draft.editingTile);
      setTileDialogOpen(true);
      const age = Math.round((Date.now() - new Date(draft.savedAt)) / 60000);
      toast.info(`Draft restored from ${age < 2 ? 'just now' : `${age} minutes ago`} — your unsaved tile has been recovered.`);
    } catch {}
  }, []); // eslint-disable-line

  const clearTileDraft = () => localStorage.removeItem(TILE_DRAFT_KEY);
  // ── end tile draft logic ───────────────────────────────────────────────

  // ── Design form draft: saves unsaved colour/font/layout changes ────────
  const DESIGN_DRAFT_KEY = user?.customer_id
    ? `taptile_design_draft_${user.customer_id}`
    : 'taptile_design_draft';
  const [designLoaded, setDesignLoaded] = useState(false);

  // Auto-save design form changes — but only after the server data has loaded
  // (prevents saving the blank initial state on first render)
  useEffect(() => {
    if (!designLoaded) return;
    localStorage.setItem(DESIGN_DRAFT_KEY, JSON.stringify({ designForm, savedAt: new Date().toISOString() }));
  }, [designForm, designLoaded]); // eslint-disable-line

  const clearDesignDraft = () => localStorage.removeItem(DESIGN_DRAFT_KEY);
  // ── end design draft logic ─────────────────────────────────────────────

  // ── 25-minute refresh warning ──────────────────────────────────────────
  const REFRESH_WARNING_KEY = 'taptile_refresh_warning_dismissed';
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(REFRESH_WARNING_KEY) === 'true') return;
    const timer = setTimeout(() => setShowRefreshWarning(true), 25 * 60 * 1000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  const dismissRefreshWarning = () => {
    if (dontShowAgain) localStorage.setItem(REFRESH_WARNING_KEY, 'true');
    setShowRefreshWarning(false);
  };
  // ── end refresh warning ────────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
    
    // Don't auto-subscribe to push notifications on load
    // Admin can manually enable in Notifications tab if needed
  }, []);

  const fetchData = async () => {
    try {
      console.log('Fetching data for user:', user?.email, 'customer_id:', user?.customer_id);
      const [tilesData, customerData, articlesData, groupsData, usersData] = await Promise.all([
        api.getTiles(),
        user?.customer_id ? api.getCustomer(user.customer_id) : Promise.resolve(null),
        api.getArticles().catch(() => []),
        api.getGroups().catch(() => []),
        api.getUsers().catch(() => [])
      ]);
      console.log('Tiles loaded:', tilesData.length, 'tiles. First tile customer_id:', tilesData[0]?.customer_id);
      setTiles(tilesData);
      setCustomer(customerData);
      setArticles(articlesData);
      setGroups(groupsData);
      setUsers(usersData);

      if (customerData) {
        // Debug: Log PWA icon URL on load
        console.log('=== LOAD DEBUG ===');
        console.log('PWA icon URL from server:', customerData.pwa_icon_url ? 
          (customerData.pwa_icon_url.length > 100 ? customerData.pwa_icon_url.substring(0, 100) + '...' : customerData.pwa_icon_url) 
          : 'EMPTY/NULL');
        console.log('PWA icon URL length:', customerData.pwa_icon_url?.length || 0);
        
        setDesignForm({
          header_text: customerData.header_text || '',
          show_header_text: customerData.show_header_text !== false,
          header_text_color: customerData.header_text_color || '#2563EB',
          header_background_color: customerData.header_background_color || customerData.primary_color || '#2563EB',
          header_text_size: customerData.header_text_size || 'xl',
          header_text_font: customerData.header_text_font || 'Inter',
          header_text_align: customerData.header_text_align || 'center',
          header_image_url: customerData.header_image_url || '',
          pwa_icon_url: customerData.pwa_icon_url || '',
          portal_domain: customerData.portal_domain || '',
          tiles_section_title: customerData.tiles_section_title || 'Your Apps & Links',
          show_tiles_section_title: customerData.show_tiles_section_title !== false,
          tiles_section_title_color: customerData.tiles_section_title_color || '#1E293B',
          social_section_title: customerData.social_section_title || 'Connect With Us',
          show_social_section_title: customerData.show_social_section_title !== false,
          social_section_title_color: customerData.social_section_title_color || '#1E293B',
          primary_color: customerData.primary_color || '#2563EB',
          background_color: customerData.background_color || '#F8FAFC',
          tile_color: customerData.tile_color || '#FFFFFF',
          social_background_color: customerData.social_background_color || '#FFFFFF',
          font_color: customerData.font_color || '#1E293B',
          font_family: customerData.font_family || 'Inter',
          tile_size: customerData.tile_size || 'default',
          tile_font_color: customerData.tile_font_color || '#1E293B',
          tile_font_size: customerData.tile_font_size || 'base',
          // NOTE: tile_style and tile_shape are NOT included here
          // They are managed by separate state variables (tileStyle, tileShape)
          show_tile_title: customerData.show_tile_title !== false,
          show_tile_icon: customerData.show_tile_icon !== false,
          grid_cols: customerData.grid_cols || 3,
          social_links_position: customerData.social_links_position || 'top',
          social_links_enabled: customerData.social_links_enabled !== false,
          news_enabled: customerData.news_enabled !== false,
          news_article_count: customerData.news_article_count || 3,
          require_authentication: customerData.require_authentication !== false,
          public_tiles_enabled: customerData.public_tiles_enabled === true,
          show_inbox_button: customerData.show_inbox_button !== false,
          show_welcome_message: customerData.show_welcome_message !== false,
          button_color: customerData.button_color || customerData.primary_color || '#2563EB',
          notification_mode: customerData.notification_mode || 'logged_in',
        });

        setTileStyle(customerData.tile_style || 'soft');
        setTileShape(customerData.tile_shape || 'default');

        // Restore any unsaved design changes from before the last refresh
        try {
          const designDraftKey = `taptile_design_draft_${user?.customer_id || ''}`;
          const raw = localStorage.getItem(designDraftKey);
          if (raw) {
            const draft = JSON.parse(raw);
            const ageMinutes = Math.round((Date.now() - new Date(draft.savedAt)) / 60000);
            // Discard drafts older than 4 hours — server value is more reliable
            if (ageMinutes > 240) {
              localStorage.removeItem(designDraftKey);
            } else if (draft?.designForm) {
              setDesignForm(draft.designForm);
              const age = Math.round((Date.now() - new Date(draft.savedAt)) / 60000);
              toast.info(`Design changes from ${age < 2 ? 'just now' : `${age} minutes ago`} restored — hit Save Changes when ready.`);
            }
          }
        } catch {}
        setDesignLoaded(true);

        if (customerData.social_links) {
          setSocialLinksForm({
            facebook: customerData.social_links.facebook || '',
            facebook_enabled: customerData.social_links.facebook_enabled || false,
            twitter: customerData.social_links.twitter || '',
            twitter_enabled: customerData.social_links.twitter_enabled || false,
            linkedin: customerData.social_links.linkedin || '',
            linkedin_enabled: customerData.social_links.linkedin_enabled || false,
            instagram: customerData.social_links.instagram || '',
            instagram_enabled: customerData.social_links.instagram_enabled || false,
            youtube: customerData.social_links.youtube || '',
            youtube_enabled: customerData.social_links.youtube_enabled || false,
            tiktok: customerData.social_links.tiktok || '',
            tiktok_enabled: customerData.social_links.tiktok_enabled || false,
            bluesky: customerData.social_links.bluesky || '',
            bluesky_enabled: customerData.social_links.bluesky_enabled || false,
            custom: customerData.social_links.custom || '',
            custom_enabled: customerData.social_links.custom_enabled || false,
            custom_label: customerData.social_links.custom_label || ''
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = tiles.findIndex((t) => t.id === active.id);
      const newIndex = tiles.findIndex((t) => t.id === over.id);
      const newTiles = arrayMove(tiles, oldIndex, newIndex);
      
      setTiles(newTiles);

      // Update order in backend
      try {
        await Promise.all(
          newTiles.map((tile, index) =>
            api.updateTile(tile.id, { order: index })
          )
        );
        toast.success('Tile order updated');
      } catch (error) {
        toast.error('Failed to update tile order');
        fetchData();
      }
    }
  };

  const handleCreateTile = async (e) => {
    e.preventDefault();
    if (!tileForm.title || !tileForm.title.trim()) {
      toast.error('Please enter a title for the tile');
      return;
    }
    if (!tileForm.icon || !String(tileForm.icon).trim()) {
      toast.error('Please pick an icon from the library, an emoji, or upload a custom image');
      return;
    }
    try {
      const tileData = {
        ...tileForm,
        tile_type: tileForm.tileType,
        url: tileForm.tileType === 'chat'
          ? `https://ubots.chat/chat/${tileForm.embed_code}`
          : tileForm.url,
        order: tiles.length,
      };
      delete tileData.tileType;
      await api.createTile(tileData);
      toast.success('Tile created');
      setTileDialogOpen(false);
      setTileForm({ title: '', url: '', icon: '', color: null, text_color: null, icon_color: null, border_color: null, visible_to_groups: [], tileType: 'link', embed_code: '' });
      clearTileDraft();
      await fetchData();
    } catch (error) {
      toast.error('Failed to create tile');
    }
  };

  const handleUpdateTile = async (e) => {
    e.preventDefault();
    if (!tileForm.title || !tileForm.title.trim()) {
      toast.error('Please enter a title for the tile');
      return;
    }
    if (!tileForm.icon || !String(tileForm.icon).trim()) {
      toast.error('Please pick an icon from the library, an emoji, or upload a custom image');
      return;
    }
    try {
      // Only send fields that the backend accepts
      const { tileType, ...rest } = tileForm;
      const tileData = {
        ...rest,
        tile_type: tileType,
        url: tileType === 'chat'
          ? `https://ubots.chat/chat/${tileForm.embed_code}`
          : tileForm.url,
      };
      console.log('Updating tile ID:', editingTile.id);
      await api.updateTile(editingTile.id, tileData);
      toast.success('Tile updated');
      setTileDialogOpen(false);
      setEditingTile(null);
      setTileForm({ title: '', url: '', icon: '', color: null, text_color: null, icon_color: null, border_color: null, visible_to_groups: [], tileType: 'link', embed_code: '' });
      clearTileDraft();
      await fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Tile update error detail:', errorDetail);
      
      // If tile not found, fetch debug info
      if (error.response?.status === 404) {
        try {
          const token = getToken();
          const debugRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/debug/tile/${editingTile.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const debugInfo = await debugRes.json();
          console.log('Debug info:', debugInfo);
          
          if (debugInfo.tile_exists && !debugInfo.match) {
            toast.error(`Tile belongs to different customer. Your customer: ${debugInfo.user_customer_id}, Tile's customer: ${debugInfo.tile_customer_id}`);
          } else if (!debugInfo.tile_exists) {
            toast.error('Tile does not exist in database');
          } else {
            toast.error(errorDetail);
          }
        } catch (debugError) {
          toast.error(errorDetail);
        }
      } else {
        toast.error(errorDetail);
      }
    }
  };

  const handleDeleteTile = async (tileId) => {
    if (!(await confirm({
      title: 'Delete tile?',
      description: 'This tile will be removed from your portal and app.',
      confirmText: 'Delete tile',
    }))) return;
    try {
      await api.deleteTile(tileId);
      toast.success('Tile deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete tile');
    }
  };

  const handleEditTile = (tile) => {
    setEditingTile(tile);
    const isPhone = tile.url?.startsWith('tel:');
    const isChat = tile.tile_type === 'chat';
    // Derive embed_code from url if not stored separately
    const embedCode = tile.embed_code || (isChat && tile.url
      ? tile.url.replace('https://ubots.chat/chat/', '').trim()
      : '');
    setTileForm({
      title: tile.title,
      url: tile.url || '',
      icon: tile.icon || '',
      color: tile.color || null,
      text_color: tile.text_color || null,
      icon_color: tile.icon_color || null,
      border_color: tile.border_color || null,
      visible_to_groups: tile.visible_to_groups || [],
      tileType: isChat ? 'chat' : isPhone ? 'phone' : 'link',
      embed_code: embedCode,
    });
    setTileDialogOpen(true);
  };

  const handleUpdateDesign = async (e) => {
    e.preventDefault();
    
    if (!user?.customer_id) {
      toast.error('Customer ID not found. Please log in again.');
      return;
    }
    
    // Debug: Log PWA icon URL before save
    console.log('=== SAVE DEBUG ===');
    console.log('PWA icon URL being saved:', designForm.pwa_icon_url ? 
      (designForm.pwa_icon_url.length > 100 ? designForm.pwa_icon_url.substring(0, 100) + '...' : designForm.pwa_icon_url) 
      : 'EMPTY');
    console.log('PWA icon URL length:', designForm.pwa_icon_url?.length || 0);
    
    try {
      const updateData = {
        header_text: designForm.header_text,
        show_header_text: designForm.show_header_text,
        header_text_color: designForm.header_text_color,
        header_background_color: designForm.header_background_color,
        header_text_size: designForm.header_text_size,
        header_text_font: designForm.header_text_font,
        header_text_align: designForm.header_text_align,
        header_image_url: designForm.header_image_url,
        pwa_icon_url: designForm.pwa_icon_url,
        portal_domain: designForm.portal_domain,
        tiles_section_title: designForm.tiles_section_title,
        show_tiles_section_title: designForm.show_tiles_section_title,
        tiles_section_title_color: designForm.tiles_section_title_color,
        social_section_title: designForm.social_section_title,
        show_social_section_title: designForm.show_social_section_title,
        social_section_title_color: designForm.social_section_title_color,
        primary_color: designForm.primary_color,
        background_color: designForm.background_color,
        tile_color: designForm.tile_color,
        social_background_color: designForm.social_background_color,
        font_color: designForm.font_color,
        font_family: designForm.font_family,
        tile_size: designForm.tile_size,
        tile_font_color: designForm.tile_font_color,
        tile_font_size: designForm.tile_font_size,
        tile_style: tileStyle,
        tile_shape: tileShape,
        show_tile_title: designForm.show_tile_title,
        show_tile_icon: designForm.show_tile_icon,
        grid_cols: designForm.grid_cols,
        social_links_position: designForm.social_links_position,
        social_links_enabled: designForm.social_links_enabled,
        news_enabled: designForm.news_enabled,
        news_article_count: designForm.news_article_count,
        social_links: {
          facebook: socialLinksForm.facebook || null,
          facebook_enabled: socialLinksForm.facebook_enabled || false,
          twitter: socialLinksForm.twitter || null,
          twitter_enabled: socialLinksForm.twitter_enabled || false,
          linkedin: socialLinksForm.linkedin || null,
          linkedin_enabled: socialLinksForm.linkedin_enabled || false,
          instagram: socialLinksForm.instagram || null,
          instagram_enabled: socialLinksForm.instagram_enabled || false,
          youtube: socialLinksForm.youtube || null,
          youtube_enabled: socialLinksForm.youtube_enabled || false,
          tiktok: socialLinksForm.tiktok || null,
          tiktok_enabled: socialLinksForm.tiktok_enabled || false,
          bluesky: socialLinksForm.bluesky || null,
          bluesky_enabled: socialLinksForm.bluesky_enabled || false,
          custom: socialLinksForm.custom || null,
          custom_enabled: socialLinksForm.custom_enabled || false,
          custom_label: socialLinksForm.custom_label || null
        },
        // Settings
        require_authentication: designForm.require_authentication,
        public_tiles_enabled: designForm.public_tiles_enabled,
        show_inbox_button: designForm.show_inbox_button,
        show_welcome_message: designForm.show_welcome_message,
        button_color: designForm.button_color,
        notification_mode: designForm.notification_mode,
      };
      
      console.log('Updating customer ID:', user.customer_id);
      console.log('Update data:', updateData);
      
      await api.updateCustomer(user.customer_id, updateData);
      toast.success('Design saved successfully!');
      clearDesignDraft();
      await fetchData();
    } catch (error) {
      console.error('Update error:', error);
      console.error('Customer ID:', user?.customer_id);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update design. Please try refreshing the page.');
    }
  };

  const handleSendNotification = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    const sendImmediately = typeof e === 'boolean' ? e : notificationForm.send_immediately;
    
    try {
      const payload = {
        ...notificationForm,
        send_immediately: sendImmediately,
        scheduled_for: sendImmediately ? null : notificationForm.scheduled_for,
        is_recurring: notificationForm.is_recurring,
        recurrence_type: notificationForm.is_recurring ? notificationForm.recurrence_type : null,
        recurrence_end_date: notificationForm.is_recurring ? notificationForm.recurrence_end_date : null
      };
      
      await api.createNotification(payload);
      const targetCount = notificationForm.recipient_ids.length + notificationForm.recipient_groups.length;
      
      if (notificationForm.is_recurring) {
        const recurrenceLabel = notificationForm.recurrence_type === 'daily' ? 'Daily' : 
                               notificationForm.recurrence_type === 'weekly' ? 'Weekly' : 'Monthly';
        toast.success(`${recurrenceLabel} recurring notification created`);
      } else if (sendImmediately) {
        toast.success(targetCount === 0 ? 'Notification sent to all users' : `Notification sent to ${targetCount} target(s)`);
      } else {
        toast.success('Notification scheduled successfully');
      }
      
      setNotificationForm({ 
        title: '', 
        message: '', 
        recipient_ids: [], 
        recipient_groups: [],
        send_immediately: true,
        scheduled_for: null,
        is_recurring: false,
        recurrence_type: null,
        recurrence_end_date: null
      });
      
      // Refresh notification history
      setNotificationRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Notification error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    }
  };

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await api.updateArticle(editingArticle.id, articleForm);
        toast.success('Article updated');
      } else {
        await api.createArticle({ ...articleForm, published: true });
        toast.success('Article created');
      }
      setArticleDialogOpen(false);
      setEditingArticle(null);
      setArticleForm({ title: '', content: '', excerpt: '', image_url: '' });
      fetchData();
    } catch (error) {
      toast.error(editingArticle ? 'Failed to update article' : 'Failed to create article');
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || '',
      image_url: article.image_url || ''
    });
    setArticleDialogOpen(true);
  };

  const handleDeleteArticle = async (articleId) => {
    if (!(await confirm({
      title: 'Delete article?',
      description: 'This article will be removed from your portal and app.',
      confirmText: 'Delete article',
    }))) return;
    try {
      await api.deleteArticle(articleId);
      toast.success('Article deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleCloneArticle = async (articleId) => {
    try {
      await api.cloneArticle(articleId);
      toast.success('Article cloned');
      fetchData();
    } catch (error) {
      toast.error('Failed to clone article');
    }
  };

  const handleMoveArticle = async (articleId, direction) => {
    try {
      await api.moveArticle(articleId, direction);
      fetchData();
    } catch (error) {
      toast.error(`Failed to move article ${direction}`);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await api.createGroup(groupForm);
      toast.success('Group created');
      setGroupDialogOpen(false);
      setGroupForm({ name: '', description: '', member_ids: [] });
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create group';
      toast.error(errorMessage);
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    try {
      await api.updateGroup(editingGroup.id, groupForm);
      toast.success('Group updated');
      setGroupDialogOpen(false);
      setEditingGroup(null);
      setGroupForm({ name: '', description: '', member_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!(await confirm({
      title: 'Delete group?',
      description: 'Users in this group will keep their accounts, but lose this group’s tiles.',
      confirmText: 'Delete group',
    }))) return;
    try {
      await api.deleteGroup(groupId);
      toast.success('Group deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || '',
      member_ids: group.member_ids || []
    });
    setGroupDialogOpen(true);
  };

  const handleUserCSVUpload = async (file) => {
    if (!file) return;
    try {
      const result = await api.uploadUsersCSV(file);
      toast.success(`Created ${result.created} user(s)`);
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} error(s) occurred. Check console for details.`);
        console.error('CSV Upload Errors:', result.errors);
      }
      fetchData();
      document.getElementById('user-csv-upload').value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    }
  };

  const handleGroupCSVUpload = async (file) => {
    if (!file) return;
    try {
      const result = await api.uploadGroupsCSV(file);
      toast.success(`Created ${result.created} group(s)`);
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} error(s) occurred. Check console for details.`);
        console.error('CSV Upload Errors:', result.errors);
      }
      fetchData();
      document.getElementById('group-csv-upload').value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    }
  };

  const downloadUserCSVTemplate = () => {
    const csv = 'name,email,password,role,groups\nJohn Doe,john@example.com,password123,user,"Marketing Team,Sales"\nJane Smith,jane@example.com,password456,admin,';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users_template.csv';
    a.click();
  };

  const downloadGroupCSVTemplate = () => {
    const csv = 'name,description,member_emails\nMarketing Team,Marketing and Communications team,"john@example.com,jane@example.com"\nSales Team,Sales department,john@example.com';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'groups_template.csv';
    a.click();
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (userForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      // Ensure customer_id is set from current user
      const userData = {
        ...userForm,
        customer_id: user?.customer_id
      };
      await api.createUser(userData);
      toast.success('User created');
      setUserDialogOpen(false);
      setUserForm({ name: '', email: '', password: '', role: 'user', groups: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (userForm.password && userForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      const updateData = { ...userForm };
      if (!updateData.password) {
        delete updateData.password; // Don't update password if not provided
      }
      await api.updateUser(editingUser.id, updateData);
      toast.success('User updated');
      setUserDialogOpen(false);
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'user', groups: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!(await confirm({
      title: 'Delete user?',
      description: 'This cannot be undone. The user will lose access immediately.',
      confirmText: 'Delete user',
    }))) return;
    try {
      await api.deleteUser(userId);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setUserForm({
      name: u.name,
      email: u.email,
      password: '', // Don't pre-fill password
      role: u.role,
      groups: u.groups || []
    });
    setUserDialogOpen(true);
  };

  const handleUnlockUser = async (email) => {
    try {
      await api.unlockUserLogin(email);
      toast.success(`Login unlocked for ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock user');
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!(await confirm({
      title: 'Suspend user?',
      description: 'They will not be able to log in until you reactivate them.',
      confirmText: 'Suspend user',
    }))) return;
    try {
      await api.suspendUser(userId);
      toast.success('User suspended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await api.activateUser(userId);
      toast.success('User activated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate user');
    }
  };

  // Check if this is an impersonation session (sessionStorage = per-tab)
  const isImpersonating = sessionStorage.getItem('impersonating') === 'true';

  return (
    <DashboardLayout title="Admin Dashboard">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm font-medium mb-4 rounded-lg flex items-center justify-center gap-2">
          <span>👁️ You are viewing this account as an impersonated admin</span>
          <button 
            onClick={() => {
              sessionStorage.removeItem('impersonating');
              sessionStorage.removeItem('token');
              window.close();
            }}
            className="ml-4 bg-amber-600 hover:bg-amber-700 px-3 py-1 rounded text-xs"
          >
            End Session & Close Tab
          </button>
        </div>
      )}
      
      {/* Sticky Save Button - visible on all tabs */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button 
          onClick={handleUpdateDesign} 
          variant="default" 
          size="lg"
          className="bg-green-600 hover:bg-green-700 shadow-2xl"
        >
          <Save className="w-5 h-5 mr-2" />
          Save Changes
        </Button>
      </div>

      <div className="mb-4 flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">Manage your mobile portal</p>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => {
            console.log('Preview clicked - current design state:', {
              tile_style: designForm.tile_style,
              tile_shape: designForm.tile_shape,
              tileStyle,
              tileShape
            });
            setPreviewOpen(true);
          }} variant="outline" size="sm" data-testid="preview-button">
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button 
            onClick={() => setSharePWAOpen(true)} 
            size="sm" 
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg"
            data-testid="share-pwa-button"
          >
            <Share2 className="w-4 h-4 mr-2" />
            📲 Share PWA
          </Button>
        </div>
      </div>

      <PreviewModal 
        open={previewOpen} 
        onOpenChange={(open) => {
          if (open) {
            console.log('=== PREVIEW MODAL OPENING ===');
            console.log('tileStyle state:', tileStyle);
            console.log('tileShape state:', tileShape);
            console.log('================================');
          }
          setPreviewOpen(open);
        }}
        customer={{
          ...customer,
          ...designForm,
          social_links: socialLinksForm,
          // FORCE override: these state values MUST take precedence
          tile_style: tileStyle,
          tile_shape: tileShape
        }}
        tiles={tiles}
        articles={articles.slice(0, designForm.news_article_count || 3)}
      />

      <Dialog open={sharePWAOpen} onOpenChange={setSharePWAOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">📲 Share Your App</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Share the native iOS app or the web version with your community
            </p>
          </DialogHeader>
          
          <div className="space-y-6">

            {/* ── OPTION 1: Native iOS App ── */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-5 text-white">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">🍎</span>
                <h4 className="font-bold text-lg">Native iPhone App</h4>
                <span className="ml-auto text-xs bg-green-500 text-white px-2 py-0.5 rounded-full font-semibold">Recommended</span>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Best experience — download directly from the App Store, then enter your organisation code.
              </p>

              {/* Org code */}
              {customer?.short_code && (
                <div className="bg-white/10 rounded-lg p-3 mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-300 mb-0.5">Organisation Code</p>
                    <p className="text-2xl font-bold tracking-widest">{customer.short_code.toUpperCase()}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    onClick={() => {
                      navigator.clipboard.writeText(customer.short_code.toUpperCase());
                      toast.success('Organisation code copied!');
                    }}
                    data-testid="copy-org-code-btn"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy Code
                  </Button>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                {/* App Store button */}
                <Button
                  className="flex-1 bg-white text-gray-900 hover:bg-gray-100 font-semibold"
                  onClick={() => window.open('https://apps.apple.com/app/id6793504755', '_blank')}
                  data-testid="app-store-link-btn"
                >
                  <span className="mr-2 text-base">🍎</span>
                  Download on the App Store
                </Button>

                {/* Copy full instructions */}
                <Button
                  variant="outline"
                  className="flex-1 border-white/30 text-white hover:bg-white/10"
                  onClick={() => {
                    const code = customer?.short_code?.toUpperCase() || 'your organisation code';
                    const msg = `Download the Taptile app from the App Store:\nhttps://apps.apple.com/app/id6793504755\n\nThen enter your organisation code: ${code}`;
                    navigator.clipboard.writeText(msg);
                    toast.success('Instructions copied — ready to paste into email or message!');
                  }}
                  data-testid="copy-ios-instructions-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Instructions
                </Button>
              </div>

              <p className="text-xs text-gray-400 mt-3">
                Step 1: Download Taptile from the App Store &nbsp;•&nbsp; Step 2: Enter code <strong className="text-white">{customer?.short_code?.toUpperCase()}</strong> &nbsp;•&nbsp; Step 3: Log in
              </p>
            </div>

            {/* ── OPTION 2: Web PWA ── */}
            <div className="border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">🌐</span>
                <h4 className="font-bold text-lg">Web Version</h4>
                <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">Works on any device</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                No download needed — works on any phone, tablet or computer via a browser link or QR code.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {/* QR Code */}
                <div className="flex flex-col items-center justify-center bg-gray-50 p-4 rounded-lg border">
                  <p className="text-sm font-medium mb-3">Scan to Open</p>
                  <div className="bg-white p-3 rounded-lg shadow">
                    <QRCodeSVG
                      value={user?.customer_id ? `${designForm.portal_domain || process.env.REACT_APP_PORTAL_URL || window.location.origin}/portal/${user.customer_id}` : (process.env.REACT_APP_PORTAL_URL || window.location.origin)}
                      size={160}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>

                {/* URL + actions */}
                <div className="space-y-3">
                  <p className="text-sm font-medium">Web Portal Link</p>
                  <code className="block bg-gray-50 px-3 py-3 rounded text-xs border break-all">
                    {user?.customer_id ? `${designForm.portal_domain || process.env.REACT_APP_PORTAL_URL || window.location.origin}/portal/${user.customer_id}` : (process.env.REACT_APP_PORTAL_URL || window.location.origin)}
                  </code>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      size="sm"
                      onClick={() => {
                        const portalUrl = user?.customer_id ? `${designForm.portal_domain || process.env.REACT_APP_PORTAL_URL || window.location.origin}/portal/${user.customer_id}` : (process.env.REACT_APP_PORTAL_URL || window.location.origin);
                        navigator.clipboard.writeText(portalUrl);
                        toast.success('Link copied!');
                      }}
                      data-testid="copy-pwa-link-btn"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const portalUrl = user?.customer_id ? `${designForm.portal_domain || process.env.REACT_APP_PORTAL_URL || window.location.origin}/portal/${user.customer_id}` : (process.env.REACT_APP_PORTAL_URL || window.location.origin);
                        window.open(portalUrl, '_blank');
                      }}
                      data-testid="open-pwa-link-btn"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Open
                    </Button>
                  </div>

                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                    <p className="text-xs font-semibold text-blue-800 mb-1">How to install the web version:</p>
                    <ul className="text-xs text-blue-700 space-y-0.5">
                      <li><strong>iPhone:</strong> Safari → Share ⬆ → Add to Home Screen</li>
                      <li><strong>Android:</strong> Chrome → Menu ⋮ → Add to Home screen</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="design" data-testid="design-tab">
            <Settings className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Design</span>
          </TabsTrigger>
          <TabsTrigger value="content" data-testid="content-tab">
            <Newspaper className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Content</span>
          </TabsTrigger>
          <TabsTrigger value="groups" data-testid="groups-tab">
            <LayoutGrid className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Groups</span>
          </TabsTrigger>
          <TabsTrigger value="notify" data-testid="notify-tab">
            <Send className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Notify</span>
          </TabsTrigger>
          <TabsTrigger value="wonde" data-testid="wonde-tab">
            <Database className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Connect</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="design" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-base sm:text-lg font-semibold">Manage Tiles</h3>
            <Dialog open={tileDialogOpen} onOpenChange={(open) => {
                setTileDialogOpen(open);
                if (!open) { setEditingTile(null); clearTileDraft(); }
              }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="create-tile-button" onClick={() => {
                  setEditingTile(null);
                  setTileForm({ title: '', url: '', icon: '', color: null, text_color: null, border_color: null, visible_to_groups: [], tileType: 'link' });
                }}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingTile ? 'Edit Tile' : 'Create Tile'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={editingTile ? handleUpdateTile : handleCreateTile} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={tileForm.title}
                      onChange={(e) => setTileForm({ ...tileForm, title: e.target.value })}
                      required
                      data-testid="tile-title-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tile-type">Tile Type</Label>
                    <select
                      id="tile-type"
                      value={tileForm.tileType}
                      onChange={(e) => {
                        const type = e.target.value;
                        setTileForm({ 
                          ...tileForm, 
                          tileType: type,
                          url: type === 'phone' ? 'tel:' : '',
                          icon: type === 'phone' ? '📞' : type === 'chat' ? '💬' : tileForm.icon,
                          embed_code: type === 'chat' ? (tileForm.embed_code || '') : tileForm.embed_code,
                        });
                      }}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="link">Website Link</option>
                      <option value="phone">Click to Call</option>
                      <option value="chat">Chat (Ubots)</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {tileForm.tileType === 'phone' ? 'Opens phone dialer when clicked'
                        : tileForm.tileType === 'chat' ? 'Opens your Ubots bot in a chat panel'
                        : 'Opens website in new tab'}
                    </p>
                  </div>
                  
                  {tileForm.tileType === 'link' ? (
                    <div>
                      <Label htmlFor="url">Website URL</Label>
                      <Input
                        id="url"
                        type="url"
                        value={tileForm.url}
                        onChange={(e) => setTileForm({ ...tileForm, url: e.target.value })}
                        required
                        data-testid="tile-url-input"
                        placeholder="https://example.com"
                      />
                    </div>
                  ) : tileForm.tileType === 'chat' ? (
                    <div className="space-y-1">
                      <Label htmlFor="embed_code">Ubots Embed Code</Label>
                      <Input
                        id="embed_code"
                        type="text"
                        value={tileForm.embed_code || ''}
                        onChange={(e) => setTileForm({ ...tileForm, embed_code: e.target.value.trim() })}
                        required
                        data-testid="tile-embed-code-input"
                        placeholder="e.g. abc123"
                      />
                      <p className="text-xs text-muted-foreground">
                        The code after <strong>/chat/</strong> in your Ubots bot URL.
                        {tileForm.embed_code && (
                          <span className="block mt-1 text-blue-600 font-mono text-xs break-all">
                            https://ubots.chat/chat/{tileForm.embed_code}
                          </span>
                        )}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={tileForm.url.replace('tel:', '')}
                        onChange={(e) => {
                          // Remove non-numeric characters except + and spaces
                          const phone = e.target.value.replace(/[^\d+\s()-]/g, '');
                          setTileForm({ ...tileForm, url: `tel:${phone}` });
                        }}
                        required
                        data-testid="tile-phone-input"
                        placeholder="+1 (555) 123-4567"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Include country code (e.g., +1 for US)
                      </p>
                    </div>
                  )}
                  <div>
                    <Label>Icon</Label>
                    <IconPicker
                      value={tileForm.icon}
                      onChange={(icon) => setTileForm({ ...tileForm, icon })}
                      onClear={() => setTileForm({ ...tileForm, icon: '' })}
                    />
                  </div>
                  <div>
                    <Label>Icon Library <span className="text-xs text-muted-foreground font-normal">— transparent, greyscale, uniform style</span></Label>
                    <div className="mt-2 rounded-lg border border-slate-200 p-3 bg-slate-50/50">
                      {isLibraryIcon(tileForm.icon) && (
                        <div className="flex items-center gap-2 mb-3 p-2 bg-white rounded border border-blue-200">
                          <LibraryIcon iconString={tileForm.icon} className="w-8 h-8" />
                          <span className="text-sm font-medium text-slate-700 flex-1">Selected from library</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setTileForm({ ...tileForm, icon: '' })}
                            className="text-red-600 hover:text-red-700 h-7"
                          >
                            <X className="w-3 h-3 mr-1" /> Clear
                          </Button>
                        </div>
                      )}
                      <IconLibraryPicker
                        selected={tileForm.icon}
                        onSelect={(icon) => setTileForm({ ...tileForm, icon })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="custom-icon">Custom Icon (Upload Image)</Label>
                    <div className="space-y-2">
                      {/* Drag and Drop Zone */}
                      <div
                        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                          tileForm.icon && (tileForm.icon.startsWith('data:') || tileForm.icon.startsWith('http'))
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-300 hover:border-primary hover:bg-primary/5'
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.add('border-primary', 'bg-primary/10');
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                        }}
                        onClick={() => tileIconInputRef.current?.click()}
                        onDrop={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                          const file = e.dataTransfer.files?.[0];
                          if (file && file.type.startsWith('image/')) {
                            try {
                              const { url } = await api.uploadTileIcon(file);
                              setTileForm({ ...tileForm, icon: url });
                              toast.success('Tile icon uploaded');
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Failed to upload tile icon');
                            }
                          }
                        }}
                      >
                        <input
                          id="custom-icon"
                          data-testid="tile-icon-file-input"
                          type="file"
                          accept="image/*"
                          ref={tileIconInputRef}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const { url } = await api.uploadTileIcon(file);
                              setTileForm({ ...tileForm, icon: url });
                              toast.success('Tile icon uploaded');
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Failed to upload tile icon');
                            } finally {
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                        />
                        {tileForm.icon && (tileForm.icon.startsWith('data:') || tileForm.icon.startsWith('http')) ? (
                          <div className="flex flex-col items-center gap-2">
                            <img 
                              src={tileForm.icon} 
                              alt="Icon preview" 
                              className="w-16 h-16 object-cover rounded-lg shadow-sm"
                            />
                            <span className="text-sm text-green-600 font-medium">Custom icon uploaded</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTileForm({ ...tileForm, icon: '' });
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Remove Icon
                            </Button>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-700">Drag & drop an image here</p>
                              <p className="text-xs text-gray-500">or click to browse</p>
                            </div>
                            <Button type="button" variant="outline" size="sm" className="pointer-events-none">
                              Browse Files
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG, or SVG (max 2MB). Or use the emoji picker above.</p>
                  </div>
                  <div>
                    <Label htmlFor="color">Tile Background Colour</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        id="color"
                        type="color"
                        value={tileForm.color || designForm.tile_color || '#FFFFFF'}
                        onChange={(e) => setTileForm({ ...tileForm, color: e.target.value })}
                        className="w-20"
                        data-testid="tile-color-input"
                      />
                      <span className="text-sm text-muted-foreground" data-testid="tile-color-value">
                        {tileForm.color || `Following theme colour (${designForm.tile_color || '#FFFFFF'})`}
                      </span>
                      {tileForm.color && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setTileForm({ ...tileForm, color: null })}
                          className="text-xs h-7"
                          title="Clear this tile's colour so it follows the global Tile Background Colour"
                          data-testid="tile-color-reset"
                        >
                          Use theme colour
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Background colour for this tile only. Overrides the global Tile Background in Design settings.</p>
                  </div>
                  <div>
                    <Label htmlFor="text-color">Tile Text Colour</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        id="text-color"
                        type="color"
                        value={tileForm.text_color || designForm.tile_font_color || '#1E293B'}
                        onChange={(e) => setTileForm({ ...tileForm, text_color: e.target.value })}
                        className="w-20"
                        data-testid="tile-text-color-input"
                      />
                      <span className="text-sm text-muted-foreground" data-testid="tile-text-color-value">
                        {tileForm.text_color || `Following theme colour (${designForm.tile_font_color || '#1E293B'})`}
                      </span>
                      {tileForm.text_color && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setTileForm({ ...tileForm, text_color: null })}
                          className="text-xs h-7"
                          title="Clear this tile's text colour so it follows the global Tile Text colour"
                          data-testid="tile-text-color-reset"
                        >
                          Use theme colour
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Colour of the tile's title label. Overrides the global Tile Text colour in Design settings.</p>
                  </div>
                  <div>
                    <Label htmlFor="icon-color">Icon Colour (library icons only)</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        id="icon-color"
                        type="color"
                        value={tileForm.icon_color || '#000000'}
                        onChange={(e) => setTileForm({ ...tileForm, icon_color: e.target.value })}
                        className="w-20"
                        data-testid="tile-icon-color-input"
                      />
                      <span className="text-sm text-muted-foreground">{tileForm.icon_color || '#000000'}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setTileForm({ ...tileForm, icon_color: '#000000' })}
                        className="text-xs h-7"
                        title="Reset the icon colour to black"
                        data-testid="tile-icon-color-reset"
                      >
                        Reset to Black
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Pick a colour for library icons, or click "Reset to Black" to return to the default. Only affects library icons — uploaded images and emojis keep their own colours.</p>
                  </div>
                  <div>
                    <Label>Visible to Groups (optional)</Label>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tileForm.visible_to_groups.length === 0}
                          onChange={(e) => {
                            // When unchecking "All Users", do nothing - user must select specific groups
                            // When checking "All Users", clear all group selections
                            if (e.target.checked) {
                              setTileForm({ ...tileForm, visible_to_groups: [] });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">All Users (no restrictions)</span>
                      </label>
                      {groups.map((group) => (
                        <label key={group.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={tileForm.visible_to_groups.includes(group.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTileForm({ ...tileForm, visible_to_groups: [...tileForm.visible_to_groups, group.id] });
                              } else {
                                setTileForm({ ...tileForm, visible_to_groups: tileForm.visible_to_groups.filter(id => id !== group.id) });
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{group.name}</span>
                          {group.group_source === 'wonde' && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Wonde</span>
                          )}
                        </label>
                      ))}
                      {groups.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-2">No groups created yet</p>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {tileForm.visible_to_groups.length === 0 
                        ? 'Visible to all users' 
                        : `Visible to ${tileForm.visible_to_groups.length} group(s)`}
                    </p>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-tile-button">
                    {editingTile ? 'Update' : 'Create'} Tile
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">Click tile to edit • Drag ⋮⋮ to reorder</p>
              <p className="text-xs text-muted-foreground opacity-70">Tap × to delete</p>
            </div>
            <div className="flex gap-1">
              {[2, 3, 4].map((cols) => (
                <button
                  key={cols}
                  onClick={() => setGridCols(cols)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    gridCols === cols
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary hover:bg-secondary/80'
                  }`}
                  data-testid={`grid-${cols}-cols`}
                >
                  {cols}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Tile Style</Label>
            <RadioGroup value={tileStyle} onValueChange={async (value) => {
              setTileStyle(value);
              // Auto-save tile style to prevent reset on fetchData
              try {
                await api.updateCustomer(user.customer_id, { tile_style: value });
              } catch (error) {
                console.error('Failed to auto-save tile style:', error);
              }
            }} className="grid grid-cols-4 gap-2">
              <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${tileStyle === 'soft' ? 'border-primary bg-primary/10' : ''}`}>
                <RadioGroupItem value="soft" className="sr-only" />
                <div className="w-12 h-12 rounded mb-1" style={{ backgroundColor: '#2563EB15' }}></div>
                <span className="text-xs">Soft</span>
              </Label>
              <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${tileStyle === 'solid' ? 'border-primary bg-primary/10' : ''}`}>
                <RadioGroupItem value="solid" className="sr-only" />
                <div className="w-12 h-12 rounded mb-1" style={{ backgroundColor: '#2563EB' }}></div>
                <span className="text-xs">Solid</span>
              </Label>
              <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${tileStyle === 'gradient' ? 'border-primary bg-primary/10' : ''}`}>
                <RadioGroupItem value="gradient" className="sr-only" />
                <div className="w-12 h-12 rounded mb-1" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #93C5FD 100%)' }}></div>
                <span className="text-xs">Gradient</span>
              </Label>
              <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${tileStyle === 'outlined' ? 'border-primary bg-primary/10' : ''}`}>
                <RadioGroupItem value="outlined" className="sr-only" />
                <div className="w-12 h-12 rounded mb-1 bg-white" style={{ border: '2px solid #2563EB' }}></div>
                <span className="text-xs">Outlined</span>
              </Label>
            </RadioGroup>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={tiles.map(t => t.id)}
            >
              <div className={`grid gap-3 ${gridCols === 2 ? 'grid-cols-2' : gridCols === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                {tiles.map((tile) => (
                  <DraggableTile
                    key={tile.id}
                    tile={tile}
                    onEdit={handleEditTile}
                    onDelete={handleDeleteTile}
                    style={tileStyle}
                    shape={tileShape}
                    tileFontColor={designForm.tile_font_color}
                    tileColor={designForm.tile_color}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {tiles.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No tiles yet. Create your first tile!
              </CardContent>
            </Card>
          )}

          <form onSubmit={handleUpdateDesign} className="space-y-6 mt-8">
            <div>
              <Label className="mb-2 block">Tile Shape</Label>
              <RadioGroup value={tileShape} onValueChange={async (value) => {
              setTileShape(value);
              // Auto-save tile shape to prevent reset on fetchData
              try {
                await api.updateCustomer(user.customer_id, { tile_shape: value });
              } catch (error) {
                console.error('Failed to auto-save tile shape:', error);
              }
            }} className="grid grid-cols-4 gap-2">
                <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${tileShape === 'default' ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="default" className="sr-only" />
                  <div className="w-12 h-12 rounded-xl bg-primary/20 mb-1"></div>
                  <span className="text-xs">Default</span>
                </Label>
                <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${tileShape === 'rounded' ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="rounded" className="sr-only" />
                  <div className="w-12 h-12 rounded-2xl bg-primary/20 mb-1"></div>
                  <span className="text-xs">Rounded</span>
                </Label>
                <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${tileShape === 'circle' ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="circle" className="sr-only" />
                  <div className="w-12 h-12 rounded-full bg-primary/20 mb-1"></div>
                  <span className="text-xs">Circle</span>
                </Label>
                <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${tileShape === 'square' ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="square" className="sr-only" />
                  <div className="w-12 h-12 bg-primary/20 mb-1"></div>
                  <span className="text-xs">Square</span>
                </Label>
              </RadioGroup>
            </div>

            <div>
              <Label className="mb-2 block">Icon Size</Label>
              <RadioGroup value={designForm.tile_size} onValueChange={(value) => setDesignForm({ ...designForm, tile_size: value })} className="grid grid-cols-4 gap-2">
                <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${designForm.tile_size === 'default' ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="default" className="sr-only" />
                  <div className="w-12 h-12 rounded-xl bg-primary/20 mb-1 flex items-center justify-center">
                    <span className="text-xs font-bold">100%</span>
                  </div>
                  <span className="text-xs">Default</span>
                </Label>
                <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${designForm.tile_size === '75' ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="75" className="sr-only" />
                  <div className="w-10 h-10 rounded-xl bg-primary/20 mb-1 flex items-center justify-center">
                    <span className="text-xs font-bold">75%</span>
                  </div>
                  <span className="text-xs">75%</span>
                </Label>
                <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${designForm.tile_size === '50' ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="50" className="sr-only" />
                  <div className="w-8 h-8 rounded-xl bg-primary/20 mb-1 flex items-center justify-center">
                    <span className="text-[10px] font-bold">50%</span>
                  </div>
                  <span className="text-xs">50%</span>
                </Label>
                <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${designForm.tile_size === '25' ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="25" className="sr-only" />
                  <div className="w-6 h-6 rounded-xl bg-primary/20 mb-1 flex items-center justify-center">
                    <span className="text-[8px] font-bold">25%</span>
                  </div>
                  <span className="text-xs">25%</span>
                </Label>
              </RadioGroup>
            </div>

            <div>
              <Label className="mb-2 block">Grid Columns</Label>
              <RadioGroup value={designForm.grid_cols.toString()} onValueChange={(value) => setDesignForm({ ...designForm, grid_cols: parseInt(value) })} className="grid grid-cols-3 gap-2">
                <Label className={`flex flex-col items-center p-3 border rounded cursor-pointer ${designForm.grid_cols === 2 ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="2" className="sr-only" />
                  <div className="flex gap-1 mb-2">
                    <div className="w-3 h-8 bg-primary/20 rounded"></div>
                    <div className="w-3 h-8 bg-primary/20 rounded"></div>
                  </div>
                  <span className="text-xs">2 Columns</span>
                </Label>
                <Label className={`flex flex-col items-center p-3 border rounded cursor-pointer ${designForm.grid_cols === 3 ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="3" className="sr-only" />
                  <div className="flex gap-1 mb-2">
                    <div className="w-2 h-8 bg-primary/20 rounded"></div>
                    <div className="w-2 h-8 bg-primary/20 rounded"></div>
                    <div className="w-2 h-8 bg-primary/20 rounded"></div>
                  </div>
                  <span className="text-xs">3 Columns</span>
                </Label>
                <Label className={`flex flex-col items-center p-3 border rounded cursor-pointer ${designForm.grid_cols === 4 ? 'border-primary bg-primary/10' : ''}`}>
                  <RadioGroupItem value="4" className="sr-only" />
                  <div className="flex gap-0.5 mb-2">
                    <div className="w-1.5 h-8 bg-primary/20 rounded"></div>
                    <div className="w-1.5 h-8 bg-primary/20 rounded"></div>
                    <div className="w-1.5 h-8 bg-primary/20 rounded"></div>
                    <div className="w-1.5 h-8 bg-primary/20 rounded"></div>
                  </div>
                  <span className="text-xs">4 Columns</span>
                </Label>
              </RadioGroup>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="text-base font-semibold">Show Tile Titles</Label>
                <p className="text-xs text-muted-foreground mt-1">Display text labels below tile icons</p>
              </div>
              <Switch
                checked={designForm.show_tile_title}
                onCheckedChange={(checked) => setDesignForm({ ...designForm, show_tile_title: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="text-base font-semibold">Show Tile Icons</Label>
                <p className="text-xs text-muted-foreground mt-1">Display icons on tiles</p>
              </div>
              <Switch
                checked={designForm.show_tile_icon}
                onCheckedChange={(checked) => setDesignForm({ ...designForm, show_tile_icon: checked })}
              />
            </div>

            {/* Colours & Fonts - Refactored Component */}
            <ColoursAndFontsCard designForm={designForm} setDesignForm={setDesignForm} />

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Header</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Header Text */}
                <div className="space-y-1">
                  <Label htmlFor="header-text" className="text-xs">Header Text</Label>
                  <Input
                    id="header-text"
                    value={designForm.header_text}
                    onChange={(e) => setDesignForm({ ...designForm, header_text: e.target.value })}
                    placeholder="Welcome to Taptile"
                    className="h-9"
                  />
                </div>

                {/* Header Colour Pickers */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Input
                      id="header-text-color"
                      type="color"
                      value={designForm.header_text_color}
                      onChange={(e) => setDesignForm({ ...designForm, header_text_color: e.target.value })}
                      className="w-10 h-8 p-0.5 cursor-pointer flex-shrink-0"
                    />
                    <Label htmlFor="header-text-color" className="text-xs cursor-pointer">Text Colour</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      id="header-background-color"
                      type="color"
                      value={designForm.header_background_color || designForm.primary_color || '#2563EB'}
                      onChange={(e) => setDesignForm({ ...designForm, header_background_color: e.target.value })}
                      className="w-10 h-8 p-0.5 cursor-pointer flex-shrink-0"
                    />
                    <Label htmlFor="header-background-color" className="text-xs cursor-pointer">Header Background</Label>
                  </div>
                </div>

                {/* Font, Size, Alignment - Three columns */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="header-text-font" className="text-xs">Font</Label>
                    <Select
                      value={designForm.header_text_font}
                      onValueChange={(value) => setDesignForm({ ...designForm, header_text_font: value })}
                    >
                      <SelectTrigger id="header-text-font" className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Open Sans">Open Sans</SelectItem>
                        <SelectItem value="Poppins">Poppins</SelectItem>
                        <SelectItem value="Montserrat">Montserrat</SelectItem>
                        <SelectItem value="Lato">Lato</SelectItem>
                        <SelectItem value="Georgia">Georgia</SelectItem>
                        <SelectItem value="Playfair Display">Playfair</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="header-text-size" className="text-xs">Size</Label>
                    <Select
                      value={designForm.header_text_size}
                      onValueChange={(value) => setDesignForm({ ...designForm, header_text_size: value })}
                    >
                      <SelectTrigger id="header-text-size" className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sm">Small</SelectItem>
                        <SelectItem value="base">Medium</SelectItem>
                        <SelectItem value="lg">Large</SelectItem>
                        <SelectItem value="xl">XL</SelectItem>
                        <SelectItem value="2xl">2XL</SelectItem>
                        <SelectItem value="3xl">3XL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="header-text-align" className="text-xs">Align</Label>
                    <Select
                      value={designForm.header_text_align}
                      onValueChange={(value) => setDesignForm({ ...designForm, header_text_align: value })}
                    >
                      <SelectTrigger id="header-text-align" className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Centre</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Show/Hide Toggle - Compact */}
                <div className="flex items-center justify-between p-2 bg-accent/50 rounded-lg">
                  <Label className="text-sm">Show Header Text</Label>
                  <Switch
                    checked={designForm.show_header_text}
                    onCheckedChange={(checked) => setDesignForm({ ...designForm, show_header_text: checked })}
                  />
                </div>

                {/* Welcome Message Toggle */}
                <div className="flex items-center justify-between p-2 bg-accent/50 rounded-lg">
                  <div>
                    <Label className="text-sm">Show Welcome Message</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Show &quot;Welcome, [name]&quot; in the header</p>
                  </div>
                  <Switch
                    checked={designForm.show_welcome_message !== false}
                    onCheckedChange={(checked) => setDesignForm({ ...designForm, show_welcome_message: checked })}
                  />
                </div>
                {/* Header Image - Compact */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="header-image" className="text-xs">Header Image</Label>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors" aria-label="Banner image tips" data-testid="header-image-tooltip">
                            <Info className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p className="font-medium mb-1">Banner image tips</p>
                          <ul className="text-xs space-y-1 list-disc pl-4">
                            <li>Use a <strong>wide image</strong> (3:1 ratio works best, e.g. <strong>1200 × 400 px</strong>).</li>
                            <li>Keep the logo and important text in the <strong>centre</strong> — edges may get cropped on phones.</li>
                            <li>Avoid square or portrait images — they crop heavily.</li>
                            <li>Supported formats: PNG, JPG, WebP.</li>
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Input
                        id="header-image-file"
                        data-testid="header-image-file-input"
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            const { url } = await api.uploadHeaderImage(file);
                            setDesignForm({ ...designForm, header_image_url: url });
                            toast.success('Header image uploaded');
                          } catch (err) {
                            console.error('Header upload failed', err);
                            toast.error(err?.response?.data?.detail || 'Failed to upload header image');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                        className="cursor-pointer h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Input
                        id="header-image-url"
                        type="url"
                        value={designForm.header_image_url?.startsWith('data:') ? '' : (designForm.header_image_url || '')}
                        onChange={(e) => setDesignForm({ ...designForm, header_image_url: e.target.value })}
                        placeholder={designForm.header_image_url?.startsWith('data:') ? 'Old base64 image (re-upload to fix)' : 'Or paste URL...'}
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>
                  {designForm.header_image_url && (
                    <div className="space-y-2">
                      <div className="w-full overflow-hidden rounded-lg border bg-gray-50" style={{ maxHeight: 120 }}>
                        <img
                          src={designForm.header_image_url}
                          alt="Header preview"
                          className="w-full object-contain"
                          style={{ maxHeight: 120 }}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground truncate">
                          {designForm.header_image_url.startsWith('data:')
                            ? 'Stored as base64 (will not survive deploys) — please re-upload'
                            : 'Stored on DigitalOcean Spaces ✓'}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          data-testid="header-image-remove-btn"
                          className="text-xs h-7 text-red-600 hover:text-red-700"
                          onClick={() => setDesignForm({ ...designForm, header_image_url: '' })}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* PWA Icon - Compact */}
                <div className="space-y-2">
                  <Label htmlFor="pwa-icon" className="text-xs">PWA Home Screen Icon</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      id="pwa-icon-file"
                      data-testid="pwa-icon-file-input"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        // Block SVG - iOS doesn't support it for PWA icons
                        if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
                          toast.error('SVG files are not supported for PWA icons. Please upload a PNG, JPG, or WebP image.');
                          e.target.value = '';
                          return;
                        }
                        // Validate image dimensions client-side first
                        try {
                          await new Promise((resolve, reject) => {
                            const img = new Image();
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              img.onload = () => {
                                if (img.width < 180 || img.height < 180) {
                                  toast.warning(`Image is ${img.width}x${img.height}px. For best results on iOS, use at least 180x180 pixels.`);
                                }
                                if (img.width !== img.height) {
                                  toast.info(`Image is not square (${img.width}x${img.height}). iOS may crop it. Square images (e.g., 192x192) work best.`);
                                }
                                resolve();
                              };
                              img.onerror = reject;
                              img.src = reader.result;
                            };
                            reader.onerror = reject;
                            reader.readAsDataURL(file);
                          });
                          const { url } = await api.uploadPwaIcon(file);
                          setDesignForm({ ...designForm, pwa_icon_url: url });
                          toast.success('PWA icon uploaded successfully');
                        } catch (err) {
                          console.error('PWA icon upload failed', err);
                          toast.error(err?.response?.data?.detail || 'Failed to upload PWA icon');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                      className="cursor-pointer h-9 text-xs"
                    />
                    <Input
                      id="pwa-icon-url"
                      type="url"
                      value={designForm.pwa_icon_url || ''}
                      onChange={(e) => {
                        const url = e.target.value;
                        // Warn if URL looks like SVG
                        if (url && (url.toLowerCase().endsWith('.svg') || url.includes('image/svg'))) {
                          toast.error('SVG files are not supported for PWA icons on iOS. Please use PNG, JPG, or WebP.');
                          return;
                        }
                        setDesignForm({ ...designForm, pwa_icon_url: url });
                      }}
                      placeholder="Or paste URL..."
                      className="h-9 text-xs"
                    />
                  </div>
                  
                  {/* iOS Requirements Warning Box */}
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800 font-medium">⚠️ iOS Requirements</p>
                    <ul className="text-xs text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                      <li>PNG, JPG, or WebP format only (no SVG)</li>
                      <li>Minimum 180x180 pixels recommended</li>
                      <li>Square images work best</li>
                    </ul>
                  </div>
                  
                  {designForm.pwa_icon_url && (
                    <div className="flex items-center gap-3 p-2 border rounded-lg bg-gray-50">
                      <img 
                        src={designForm.pwa_icon_url} 
                        alt="PWA icon" 
                        className="h-12 w-12 object-cover rounded-lg border"
                        onError={(e) => {
                          e.target.onerror = null;
                          toast.error('Failed to load icon image. The URL may be invalid or the image format unsupported.');
                        }}
                      />
                      <div className="flex-1">
                        <span className="text-xs font-medium text-green-600">✓ Icon set</span>
                        <p className="text-xs text-muted-foreground">Will appear on iOS/Android home screens</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDesignForm({ ...designForm, pwa_icon_url: '' })}
                        className="text-red-500 hover:text-red-700 h-8"
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Portal Domain - For QR codes and links */}
                <div className="space-y-2">
                  <Label htmlFor="portal-domain" className="text-xs">Portal Domain (for QR codes)</Label>
                  <Input
                    id="portal-domain"
                    type="url"
                    value={designForm.portal_domain || ''}
                    onChange={(e) => setDesignForm({ ...designForm, portal_domain: e.target.value })}
                    placeholder="https://taptile.app"
                    className="h-9 text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Set your custom domain for QR codes (e.g. <code>https://portal.yourschool.org</code>). Leave empty to use <strong>taptile.app</strong>.
                  </p>
                </div>
                
                {/* Tiles Section Title - Compact */}
                <div className="flex items-center justify-between p-2 bg-accent/50 rounded-lg">
                  <Label className="text-sm">Show Tiles Section Title</Label>
                  <Switch
                    checked={designForm.show_tiles_section_title}
                    onCheckedChange={(checked) => setDesignForm({ ...designForm, show_tiles_section_title: checked })}
                  />
                </div>

                {designForm.show_tiles_section_title && (
                  <div className="flex gap-2">
                    <Input
                      id="tiles-section-title"
                      value={designForm.tiles_section_title}
                      onChange={(e) => setDesignForm({ ...designForm, tiles_section_title: e.target.value })}
                      placeholder="Your Apps & Links"
                      className="flex-1 h-9"
                    />
                    <Input
                      id="tiles-title-color"
                      type="color"
                      value={designForm.tiles_section_title_color}
                      onChange={(e) => setDesignForm({ ...designForm, tiles_section_title_color: e.target.value })}
                      className="w-12 h-9 p-0.5 cursor-pointer"
                      title="Title colour"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Social Media & News</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enable Social Links</Label>
                  <Switch
                    checked={designForm.social_links_enabled}
                    onCheckedChange={(checked) => setDesignForm({ ...designForm, social_links_enabled: checked })}
                  />
                </div>

                {designForm.social_links_enabled && (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Show Section Title</Label>
                        <Switch
                          checked={designForm.show_social_section_title}
                          onCheckedChange={(checked) => setDesignForm({ ...designForm, show_social_section_title: checked })}
                        />
                      </div>
                    </div>

                    {designForm.show_social_section_title && (
                      <div className="space-y-2">
                        <Label htmlFor="social-section-title">Social Section Title</Label>
                        <div className="flex gap-2">
                          <Input
                            id="social-section-title"
                            type="text"
                            value={designForm.social_section_title}
                            onChange={(e) => setDesignForm({ ...designForm, social_section_title: e.target.value })}
                            placeholder="Connect With Us"
                            className="flex-1"
                          />
                          <Input
                            id="social-title-color"
                            type="color"
                            value={designForm.social_section_title_color}
                            onChange={(e) => setDesignForm({ ...designForm, social_section_title_color: e.target.value })}
                            className="w-14 h-10"
                            title="Title colour"
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Social Icons Position</Label>
                      <RadioGroup value={designForm.social_links_position} onValueChange={(value) => setDesignForm({ ...designForm, social_links_position: value })} className="grid grid-cols-3 gap-2">
                        <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${designForm.social_links_position === 'top' ? 'border-primary bg-primary/10' : ''}`}>
                          <RadioGroupItem value="top" className="sr-only" />
                          <span className="text-xs text-center">Below Header</span>
                        </Label>
                        <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${designForm.social_links_position === 'between' ? 'border-primary bg-primary/10' : ''}`}>
                          <RadioGroupItem value="between" className="sr-only" />
                          <span className="text-xs text-center">Below Tiles</span>
                        </Label>
                        <Label className={`flex flex-col items-center p-2 border rounded cursor-pointer ${designForm.social_links_position === 'bottom' ? 'border-primary bg-primary/10' : ''}`}>
                          <RadioGroupItem value="bottom" className="sr-only" />
                          <span className="text-xs text-center">Below News</span>
                        </Label>
                      </RadioGroup>
                    </div>

                    {['facebook', 'twitter', 'linkedin', 'instagram', 'youtube', 'tiktok', 'bluesky'].map((platform) => (
                      <div key={platform} className="space-y-2 p-3 bg-accent/50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <Label className="capitalize">{platform === 'twitter' ? 'X' : platform === 'tiktok' ? 'TikTok' : platform === 'bluesky' ? 'Bluesky' : platform}</Label>
                          <Switch
                            checked={socialLinksForm[`${platform}_enabled`]}
                            onCheckedChange={(checked) => setSocialLinksForm({ ...socialLinksForm, [`${platform}_enabled`]: checked })}
                            data-testid={`social-${platform}-toggle`}
                          />
                        </div>
                        {socialLinksForm[`${platform}_enabled`] && (
                          <Input
                            type="url"
                            value={socialLinksForm[platform]}
                            onChange={(e) => setSocialLinksForm({ ...socialLinksForm, [platform]: e.target.value })}
                            placeholder={platform === 'twitter' ? 'https://x.com/yourprofile' : platform === 'tiktok' ? 'https://tiktok.com/@yourprofile' : platform === 'bluesky' ? 'https://bsky.app/profile/yourhandle' : `https://${platform}.com/yourprofile`}
                            className="text-sm"
                            data-testid={`social-${platform}-input`}
                          />
                        )}
                      </div>
                    ))}

                    {/* Custom link — for any platform not in the built-in list */}
                    <div className="space-y-2 p-3 bg-accent/50 rounded-lg border border-dashed">
                      <div className="flex items-center justify-between">
                        <Label>Custom Link</Label>
                        <Switch
                          checked={socialLinksForm.custom_enabled}
                          onCheckedChange={(checked) => setSocialLinksForm({ ...socialLinksForm, custom_enabled: checked })}
                          data-testid="social-custom-toggle"
                        />
                      </div>
                      {socialLinksForm.custom_enabled && (
                        <div className="space-y-2">
                          <Input
                            type="text"
                            value={socialLinksForm.custom_label || ''}
                            onChange={(e) => setSocialLinksForm({ ...socialLinksForm, custom_label: e.target.value })}
                            placeholder="Label (e.g. Discord)"
                            className="text-sm"
                            maxLength={20}
                            data-testid="social-custom-label"
                          />
                          <Input
                            type="url"
                            value={socialLinksForm.custom || ''}
                            onChange={(e) => setSocialLinksForm({ ...socialLinksForm, custom: e.target.value })}
                            placeholder="https://your-link.com"
                            className="text-sm"
                            data-testid="social-custom-input"
                          />
                          <p className="text-xs text-muted-foreground">Uses a generic chain-link icon. The label shows on hover.</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="pt-4 border-t space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Enable News Feed</Label>
                    <Switch
                      checked={designForm.news_enabled}
                      onCheckedChange={(checked) => setDesignForm({ ...designForm, news_enabled: checked })}
                    />
                  </div>
                  
                  {designForm.news_enabled && (
                    <div>
                      <Label className="mb-2 block">Articles to Display in PWA</Label>
                      <div className="flex gap-2">
                        {[1, 2, 3].map((count) => (
                          <button
                            key={count}
                            type="button"
                            onClick={() => setDesignForm({ ...designForm, news_article_count: count })}
                            className={`flex-1 px-4 py-2 rounded-md border transition-colors ${
                              designForm.news_article_count === count
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background hover:bg-accent border-border'
                            }`}
                          >
                            {count} Article{count > 1 ? 's' : ''}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Choose how many articles appear in the user PWA (top {designForm.news_article_count} from list)
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Access Control & Security - Refactored Component */}
            <AccessControlCard designForm={designForm} setDesignForm={setDesignForm} />

          </form>
        </TabsContent>

        <TabsContent value="content" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">News Articles</h3>
            <Dialog open={articleDialogOpen} onOpenChange={(open) => {
              setArticleDialogOpen(open);
              if (!open) {
                setEditingArticle(null);
                setArticleForm({ title: '', content: '', excerpt: '', image_url: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Article
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" key={editingArticle?.id || 'new'}>
                <DialogHeader>
                  <DialogTitle>{editingArticle ? 'Edit Article' : 'Create Article'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateArticle} className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Excerpt (optional)</Label>
                    <Textarea
                      value={articleForm.excerpt}
                      onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                      rows={2}
                      placeholder="A brief summary or preview of the article..."
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Short preview text shown before the full article content (1-2 sentences recommended)
                    </p>
                  </div>
                  <div>
                    <Label>Content</Label>
                    <Textarea
                      value={articleForm.content}
                      onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                      required
                      rows={6}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label>Article Image (optional)</Label>
                    
                    {/* Image Preview */}
                    {articleForm.image_url && (
                      <div className="relative border-2 border-dashed border-primary rounded-lg p-2 bg-primary/5">
                        {/* Debug info */}
                        <div className="text-xs text-gray-500 mb-1 break-all">
                          Preview URL: {getImageUrl(articleForm.image_url)}
                        </div>
                        <img 
                          src={getImageUrl(articleForm.image_url)}
                          alt="Preview" 
                          className="w-full h-48 object-contain rounded bg-white"
                          onLoad={(e) => {
                            console.log('✅ Image loaded successfully:', getImageUrl(articleForm.image_url));
                          }}
                          onError={(e) => {
                            console.error('❌ Image failed to load:', getImageUrl(articleForm.image_url));
                            console.error('Full image URL:', e.target.src);
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="100" viewBox="0 0 200 100"%3E%3Crect width="200" height="100" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial" font-size="12"%3EImage failed to load%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            console.log('Removing image from article');
                            setArticleForm({ ...articleForm, image_url: '' });
                            // Clear file input
                            const fileInput = document.getElementById('article-image-upload');
                            if (fileInput) fileInput.value = '';
                          }}
                          className="absolute top-3 right-3 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 shadow-lg"
                          title="Remove image"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className="absolute bottom-3 left-3 bg-green-600 text-white px-2 py-1 rounded text-xs font-medium">
                          ✓ {(articleForm.image_url.startsWith('/uploads/') || articleForm.image_url.startsWith('/api/uploads/')) ? 'Uploaded' : 'External URL'}
                        </div>
                      </div>
                    )}
                    
                    {/* Upload Button and Image Library */}
                    <div className="flex gap-2">
                      <label className="flex-1">
                        <div className="cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-primary hover:bg-primary/5 transition-colors text-center">
                          <ImageIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <span className="text-sm text-gray-600">Click to upload image</span>
                          <Input
                            id="article-image-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            key={editingArticle?.id || 'new-article'}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  toast.info('Uploading image...');
                                  const { url } = await api.uploadArticleImage(file);
                                  console.log('Upload response URL:', url);
                                  console.log('Current articleForm:', articleForm);
                                  console.log('Editing article ID:', editingArticle?.id);
                                  const newFormState = { ...articleForm, image_url: url };
                                  console.log('New form state:', newFormState);
                                  setArticleForm(newFormState);
                                  toast.success('Image uploaded successfully!');
                                  // Force a small delay to ensure state update
                                  setTimeout(() => {
                                    console.log('Image URL after state update:', newFormState.image_url);
                                    console.log('Current form state:', articleForm);
                                  }, 100);
                                } catch (error) {
                                  console.error('Upload error:', error);
                                  toast.error('Failed to upload image');
                                  e.target.value = '';
                                }
                              }
                            }}
                          />
                        </div>
                      </label>
                    </div>
                    
                    {/* Image Library Button */}
                    <div className="flex items-center gap-2">
                      <ImageLibrary 
                        onSelectImage={(url) => setArticleForm({ ...articleForm, image_url: url })}
                        selectedImage={articleForm.image_url}
                      />
                      <span className="text-xs text-muted-foreground">Choose from previously uploaded images</span>
                    </div>
                    
                    {/* Manual URL Input */}
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1">Or paste image URL</Label>
                      <Input
                        type="text"
                        value={articleForm.image_url}
                        onChange={(e) => setArticleForm({ ...articleForm, image_url: e.target.value })}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">
                    {editingArticle ? 'Update Article' : 'Create Article'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Newspaper className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm text-blue-900 mb-1">PWA Display Settings</h4>
                      <p className="text-xs text-blue-700 mb-3">
                        Control how many articles appear in the user PWA. The first article is featured. Use move up/down buttons to reorder.
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-semibold mb-2 block text-blue-900">Number of Articles to Display in PWA</Label>
                    <div className="flex gap-2">
                      {[1, 2, 3].map((count) => (
                        <button
                          key={count}
                          type="button"
                          onClick={async () => {
                            const newForm = { ...designForm, news_article_count: count };
                            setDesignForm(newForm);
                            // Auto-save this setting
                            try {
                              await api.updateCustomer(user.customer_id, { news_article_count: count });
                              toast.success(`PWA will now show ${count} article${count > 1 ? 's' : ''}`);
                            } catch (error) {
                              toast.error('Failed to update setting');
                            }
                          }}
                          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all font-medium ${
                            designForm.news_article_count === count
                              ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                              : 'bg-white hover:bg-blue-50 border-blue-200 text-blue-900'
                          }`}
                        >
                          <div className="text-lg">{count}</div>
                          <div className="text-xs mt-1">Article{count > 1 ? 's' : ''}</div>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-blue-600 rounded-full"></span>
                      Currently showing top <strong>{designForm.news_article_count}</strong> article{designForm.news_article_count > 1 ? 's' : ''} in PWA
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {articles.length > 0 ? (
              <div className="space-y-3">
                {articles.map((article, index) => (
                  <Card key={article.id} className={index < (designForm.news_article_count || 3) ? 'border-primary/30 bg-primary/5' : ''}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMoveArticle(article.id, 'up')}
                            disabled={index === 0}
                            className="h-7 w-7 p-0"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMoveArticle(article.id, 'down')}
                            disabled={index === articles.length - 1}
                            className="h-7 w-7 p-0"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-sm truncate">{article.title}</h4>
                                {index < (designForm.news_article_count || 3) && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                                    PWA {index === 0 ? '★ Featured' : `#${index + 1}`}
                                  </span>
                                )}
                              </div>
                              {article.excerpt && (
                                <p className="text-xs text-muted-foreground line-clamp-2">{article.excerpt}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                            <Calendar className="w-3 h-3" />
                            <span>Created {new Date(article.created_at).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>By {article.author}</span>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditArticle(article)}
                              className="h-8"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCloneArticle(article.id)}
                              className="h-8"
                            >
                              <CopyIcon className="w-3 h-3 mr-1" />
                              Clone
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteArticle(article.id)}
                              className="h-8 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Newspaper className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No articles yet. Create your first article!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="groups" className="space-y-6">
          {/* Users Management Section */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-base">Manage Users</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">{users.length} user(s) in your organization</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={downloadUserCSVTemplate}
                  >
                    📥 CSV Template
                  </Button>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleUserCSVUpload(e.target.files[0])}
                    style={{ display: 'none' }}
                    id="user-csv-upload"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => document.getElementById('user-csv-upload').click()}
                  >
                    📤 Upload CSV
                  </Button>
                  <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" onClick={() => {
                        setEditingUser(null);
                        setUserForm({ name: '', email: '', password: '', role: 'user', groups: [] });
                      }}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="space-y-4">
                        <div>
                          <Label>Full Name</Label>
                          <Input
                            value={userForm.name}
                            onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                            placeholder="John Doe"
                            required
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={userForm.email}
                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            placeholder="john@example.com"
                            required
                            disabled={!!editingUser}
                          />
                        </div>
                        <div>
                          <Label>Password {editingUser && '(leave blank to keep current)'}</Label>
                          <Input
                            type="password"
                            value={userForm.password}
                            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                            placeholder="Enter password"
                            required={!editingUser}
                            minLength={8}
                            data-testid="user-password-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters</p>
                        </div>
                        <div>
                          <Label>Role</Label>
                          <select
                            value={userForm.role}
                            onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                        <div>
                          <Label>Assign to Groups (optional)</Label>
                          <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                            {groups.map((group) => (
                              <label key={group.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  checked={userForm.groups.includes(group.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setUserForm({ ...userForm, groups: [...userForm.groups, group.id] });
                                    } else {
                                      setUserForm({ ...userForm, groups: userForm.groups.filter(id => id !== group.id) });
                                    }
                                  }}
                                  className="rounded"
                                />
                                <span>{group.name}</span>
                              </label>
                            ))}
                            {groups.length === 0 && (
                              <p className="text-xs text-muted-foreground text-center py-2">No groups available</p>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">{userForm.groups.length} group(s) selected</p>
                        </div>
                        <Button type="submit" className="w-full">
                          {editingUser ? 'Update' : 'Create'} User
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  No users yet. Create your first user!
                </div>
              ) : (
                <div className="space-y-3">
                  {users.map((u) => (
                    <div key={u.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{u.name}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
                              u.role === 'super_admin' ? 'bg-red-100 text-red-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {u.role.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{u.email}</p>
                          {u.groups && u.groups.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {u.groups.map(groupId => {
                                const group = groups.find(g => g.id === groupId);
                                return group ? (
                                  <span key={groupId} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                                    {group.name}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditUser(u)}
                          >
                            Edit
                          </Button>
                          {u.role !== 'super_admin' && u.id !== user?.id && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnlockUser(u.email)}
                                className="text-blue-600 hover:text-blue-700"
                                title="Clear login lockout if user is locked out"
                              >
                                Unlock
                              </Button>
                              {u.is_active !== false ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSuspendUser(u.id)}
                                  className="text-orange-600 hover:text-orange-700"
                                >
                                  Suspend
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleActivateUser(u.id)}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  Activate
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteUser(u.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Groups Management Section */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Manage Groups</h3>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={downloadGroupCSVTemplate}
              >
                📥 Template
              </Button>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => handleGroupCSVUpload(e.target.files[0])}
                style={{ display: 'none' }}
                id="group-csv-upload"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => document.getElementById('group-csv-upload').click()}
              >
                📤 Upload CSV
              </Button>
              <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" onClick={() => {
                    setEditingGroup(null);
                    setGroupForm({ name: '', description: '', member_ids: [] });
                  }}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Group
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingGroup ? 'Edit Group' : 'Create Group'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={editingGroup ? handleUpdateGroup : handleCreateGroup} className="space-y-4">
                  <div>
                    <Label>Group Name</Label>
                    <Input
                      value={groupForm.name}
                      onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                      placeholder="e.g., Marketing Team, Teachers"
                      required
                      disabled={editingGroup?.group_source === 'wonde'}
                    />
                  </div>
                  <div>
                    <Label>Description (optional)</Label>
                    <Textarea
                      value={groupForm.description}
                      onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                      placeholder="Describe the group..."
                      rows={3}
                      disabled={editingGroup?.group_source === 'wonde'}
                    />
                  </div>
                  <div>
                    <Label>Members</Label>
                    <div className="border rounded-lg p-3 max-h-60 overflow-y-auto space-y-2">
                      {users.map((u) => (
                        <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={groupForm.member_ids.includes(u.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setGroupForm({ ...groupForm, member_ids: [...groupForm.member_ids, u.id] });
                              } else {
                                setGroupForm({ ...groupForm, member_ids: groupForm.member_ids.filter(id => id !== u.id) });
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{u.name} ({u.email})</span>
                        </label>
                      ))}
                      {users.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No users available</p>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{groupForm.member_ids.length} member(s) selected</p>
                  </div>
                  {editingGroup?.group_source === 'wonde' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        ℹ️ This group is synced from Wonde. You can only modify members.
                      </p>
                    </div>
                  )}
                  <Button type="submit" className="w-full">
                    {editingGroup ? 'Update' : 'Create'} Group
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {groups.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No groups yet. Create your first group to organize users!
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {groups.map((group) => (
                <Card key={group.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{group.name}</h4>
                          {group.group_source === 'wonde' && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Wonde</span>
                          )}
                          {group.group_source === 'manual' && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Manual</span>
                          )}
                        </div>
                        {group.description && (
                          <p className="text-sm text-muted-foreground mb-2">{group.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {group.member_ids?.length || 0} member(s)
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditGroup(group)}
                        >
                          Edit
                        </Button>
                        {group.group_source !== 'wonde' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteGroup(group.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notify" className="space-y-4">
          {/* Push Notification Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Push Notification Settings</CardTitle>
              <p className="text-sm text-muted-foreground">Configure who can receive push notifications</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label className="font-medium">Who can receive notifications?</Label>
                
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    designForm.notification_mode === 'logged_in' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setDesignForm({ ...designForm, notification_mode: 'logged_in' })}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="notification_mode"
                      checked={designForm.notification_mode === 'logged_in'}
                      onChange={() => setDesignForm({ ...designForm, notification_mode: 'logged_in' })}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium">🔐 Logged-in Users Only</p>
                      <p className="text-sm text-muted-foreground">
                        Only users who have signed in can receive push notifications. 
                        Best for schools, organisations, or apps with user accounts.
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    designForm.notification_mode === 'all_devices' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setDesignForm({ ...designForm, notification_mode: 'all_devices' })}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="notification_mode"
                      checked={designForm.notification_mode === 'all_devices'}
                      onChange={() => setDesignForm({ ...designForm, notification_mode: 'all_devices' })}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium">📱 All Devices (No Login Required)</p>
                      <p className="text-sm text-muted-foreground">
                        Anyone who installs the PWA can receive notifications without signing in. 
                        Best for public events, community apps, or general announcements.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-900">
                  <strong>Current Mode:</strong> {
                    designForm.notification_mode === 'logged_in'
                      ? '🔐 Only logged-in users will receive notifications'
                      : '📱 All devices with the PWA installed will receive notifications'
                  }
                </p>
              </div>

              <Button 
                type="button" 
                onClick={handleUpdateDesign}
                className="w-full"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Notification Settings
              </Button>
            </CardContent>
          </Card>

          {/* Notification History */}
          {/* Push Notification Debug - Temporary */}
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="text-base text-orange-700">🔧 Push Notification Debug</CardTitle>
              <p className="text-sm text-orange-600">Check and test push notification setup</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                size="sm"
                className="w-full"
                onClick={async () => {
                  try {
                    const result = await api.debugPushSubscriptions();
                    toast.info(
                      <div className="text-xs space-y-1">
                        <p><strong>Total Subscriptions:</strong> {result.total_subscriptions}</p>
                        <p><strong>You subscribed:</strong> {result.current_user_subscribed ? '✅ Yes' : '❌ No'}</p>
                        <p><strong>VAPID configured:</strong> {result.vapid_configured ? '✅ Yes' : '❌ No'}</p>
                        <hr className="my-2" />
                        <p><strong>Subscribed Users:</strong></p>
                        {result.all_subscriptions?.length === 0 && <p className="text-muted-foreground">No subscriptions found</p>}
                        {result.all_subscriptions?.map((s, i) => (
                          <p key={i} className="pl-2">• {s.user_name || 'Anonymous'} {s.user_email ? `(${s.user_email})` : ''}</p>
                        ))}
                      </div>,
                      { duration: 15000 }
                    );
                  } catch (error) {
                    toast.error('Debug endpoint not available: ' + error.message);
                  }
                }}
              >
                Check Subscriptions
              </Button>
              <Button 
                variant="default" 
                size="sm"
                className="w-full bg-orange-500 hover:bg-orange-600"
                onClick={async () => {
                  try {
                    const result = await api.testPushNotification();
                    if (result.success) {
                      toast.success('Test notification sent! Check your device.');
                    } else {
                      toast.error('Failed: ' + result.error);
                    }
                  } catch (error) {
                    toast.error('Test failed: ' + error.message);
                  }
                }}
              >
                Send Test Push to Myself
              </Button>
              <div className="pt-2 border-t">
                <p className="text-xs text-orange-700 mb-2">Test push to a specific user:</p>
                <div className="flex gap-2">
                  <select 
                    id="test-push-user-select"
                    className="flex-1 text-xs p-2 border rounded"
                  >
                    <option value="">Select a user...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      const userId = document.getElementById('test-push-user-select').value;
                      if (!userId) {
                        toast.error('Please select a user first');
                        return;
                      }
                      try {
                        const result = await api.testPushToUser(userId);
                        if (result.success) {
                          toast.success('✅ Test push sent! Check the user\'s device.');
                        } else {
                          toast.error('❌ Failed: ' + result.error);
                        }
                      } catch (error) {
                        toast.error('Test failed: ' + error.message);
                      }
                    }}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notification History</CardTitle>
              <p className="text-sm text-muted-foreground">View all sent notifications</p>
            </CardHeader>
            <CardContent>
              <NotificationHistory customer={customer} users={users} groups={groups} refreshKey={notificationRefreshKey} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Send Notification</CardTitle>
              <p className="text-sm text-muted-foreground">Send notifications to specific users or groups</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendNotification} className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={notificationForm.title}
                    onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                    placeholder="e.g., System Maintenance, New Feature"
                    required
                  />
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea
                    value={notificationForm.message}
                    onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
                    placeholder="Enter your notification message..."
                    required
                    rows={4}
                  />
                </div>
                
                <div>
                  <Label>Send To</Label>
                  <div className="space-y-3 mt-2">
                    <div className="border rounded-lg p-3">
                      <label className="flex items-center gap-2 cursor-pointer mb-3 pb-3 border-b">
                        <input
                          type="radio"
                          name="notification-target"
                          checked={notificationForm.recipient_ids.length === 0 && notificationForm.recipient_groups.length === 0}
                          onChange={() => {
                            setNotificationForm({ ...notificationForm, recipient_ids: [], recipient_groups: [] });
                          }}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">All Users</span>
                      </label>
                      
                      <label className="flex items-center gap-2 cursor-pointer mb-3">
                        <input
                          type="radio"
                          name="notification-target"
                          checked={notificationForm.recipient_ids.length > 0 || notificationForm.recipient_groups.length > 0}
                          onChange={() => {
                            // Just toggle the radio, user will select below
                          }}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">Specific Users or Groups</span>
                      </label>
                      
                      {(notificationForm.recipient_ids.length > 0 || notificationForm.recipient_groups.length > 0 || true) && (
                        <>
                          <div className="mb-3 ml-6">
                            <p className="text-xs font-medium text-muted-foreground mb-2">Select Users:</p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {users.map((u) => (
                                <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                  <input
                                    type="checkbox"
                                    checked={notificationForm.recipient_ids.includes(u.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setNotificationForm({ ...notificationForm, recipient_ids: [...notificationForm.recipient_ids, u.id] });
                                      } else {
                                        setNotificationForm({ ...notificationForm, recipient_ids: notificationForm.recipient_ids.filter(id => id !== u.id) });
                                      }
                                    }}
                                    className="rounded"
                                  />
                                  <span>{u.name} ({u.email})</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          <div className="ml-6">
                            <p className="text-xs font-medium text-muted-foreground mb-2">Select Groups:</p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {groups.map((group) => (
                                <label key={group.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                  <input
                                    type="checkbox"
                                    checked={notificationForm.recipient_groups.includes(group.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setNotificationForm({ ...notificationForm, recipient_groups: [...notificationForm.recipient_groups, group.id] });
                                      } else {
                                        setNotificationForm({ ...notificationForm, recipient_groups: notificationForm.recipient_groups.filter(id => id !== group.id) });
                                      }
                                    }}
                                    className="rounded"
                                  />
                                  <span>{group.name} ({group.member_ids?.length || 0} members)</span>
                                </label>
                              ))}
                              {groups.length === 0 && (
                                <p className="text-xs text-muted-foreground">No groups created yet</p>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {notificationForm.recipient_ids.length === 0 && notificationForm.recipient_groups.length === 0
                      ? 'Will be sent to all users'
                      : `${notificationForm.recipient_ids.length} user(s) and ${notificationForm.recipient_groups.length} group(s) selected`}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="send-now"
                        checked={notificationForm.send_immediately}
                        onChange={() => setNotificationForm({ ...notificationForm, send_immediately: true, scheduled_for: null })}
                        className="rounded"
                      />
                      <label htmlFor="send-now" className="text-sm font-medium cursor-pointer">Send Now</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="schedule"
                        checked={!notificationForm.send_immediately}
                        onChange={() => setNotificationForm({ ...notificationForm, send_immediately: false })}
                        className="rounded"
                      />
                      <label htmlFor="schedule" className="text-sm font-medium cursor-pointer">Schedule</label>
                    </div>
                  </div>

                  {!notificationForm.send_immediately && (
                    <div>
                      <Label>Schedule For</Label>
                      <Input
                        type="datetime-local"
                        value={notificationForm.scheduled_for || ''}
                        onChange={(e) => setNotificationForm({ ...notificationForm, scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : null })}
                        min={new Date().toISOString().slice(0, 16)}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Your local timezone will be used
                      </p>
                    </div>
                  )}

                  {/* Recurring Notification Options */}
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        id="is-recurring"
                        checked={notificationForm.is_recurring}
                        onChange={(e) => setNotificationForm({ 
                          ...notificationForm, 
                          is_recurring: e.target.checked,
                          recurrence_type: e.target.checked ? 'daily' : null
                        })}
                        className="rounded"
                      />
                      <label htmlFor="is-recurring" className="text-sm font-medium cursor-pointer">
                        🔄 Make this a recurring notification
                      </label>
                    </div>

                    {notificationForm.is_recurring && (
                      <div className="space-y-3 mt-3 pt-3 border-t border-purple-200">
                        <div>
                          <Label className="text-sm">Repeat</Label>
                          <div className="flex gap-2 mt-1">
                            {[
                              { value: 'daily', label: '📅 Daily' },
                              { value: 'weekly', label: '📆 Weekly' },
                              { value: 'monthly', label: '🗓️ Monthly' }
                            ].map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setNotificationForm({ ...notificationForm, recurrence_type: option.value })}
                                className={`flex-1 px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                                  notificationForm.recurrence_type === option.value
                                    ? 'border-purple-500 bg-purple-100 text-purple-700 font-medium'
                                    : 'border-gray-200 hover:border-purple-300 bg-white'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm">End Date (Optional)</Label>
                          <Input
                            type="date"
                            value={notificationForm.recurrence_end_date ? notificationForm.recurrence_end_date.split('T')[0] : ''}
                            onChange={(e) => setNotificationForm({ 
                              ...notificationForm, 
                              recurrence_end_date: e.target.value ? new Date(e.target.value).toISOString() : null 
                            })}
                            min={new Date().toISOString().split('T')[0]}
                            className="mt-1"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Leave empty to repeat indefinitely
                          </p>
                        </div>

                        <div className="p-2 bg-white rounded border text-xs text-gray-600">
                          <strong>Preview:</strong> This notification will be sent{' '}
                          <span className="text-purple-600 font-medium">
                            {notificationForm.recurrence_type === 'daily' && 'every day'}
                            {notificationForm.recurrence_type === 'weekly' && 'every week'}
                            {notificationForm.recurrence_type === 'monthly' && 'every month'}
                          </span>
                          {notificationForm.recurrence_end_date && (
                            <> until <span className="text-purple-600 font-medium">{new Date(notificationForm.recurrence_end_date).toLocaleDateString()}</span></>
                          )}
                          {!notificationForm.recurrence_end_date && <> indefinitely</>}
                        </div>
                      </div>
                    )}
                  </div>

                  <Button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSendNotification(notificationForm.send_immediately);
                    }} 
                    className="w-full"
                    disabled={!notificationForm.send_immediately && !notificationForm.scheduled_for}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {notificationForm.is_recurring 
                      ? 'Create Recurring Notification' 
                      : notificationForm.send_immediately 
                        ? 'Send Now' 
                        : 'Schedule Notification'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Wonde Integration Tab */}
        <TabsContent value="wonde" className="space-y-4">
          <NexusCmsIntegration
            customer={customer}
            onCustomerUpdate={fetchData}
          />
          <WondeIntegration 
            customer={customer} 
            onCustomerUpdate={fetchData}
          />
        </TabsContent>
      </Tabs>

      {/* ── 25-minute refresh warning popup ── */}
      <Dialog open={showRefreshWarning} onOpenChange={setShowRefreshWarning}>
        <DialogContent className="max-w-sm" data-testid="refresh-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-yellow-500">&#9888;</span> Page Refresh Approaching
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Your session will auto-refresh in approximately <strong>5 minutes</strong>. Any unsaved changes to tiles or design settings will be preserved in your browser, but it's a good idea to save now.
            </p>
            <div className="flex items-center gap-2">
              <input
                id="dontShowAgain"
                type="checkbox"
                className="rounded border-gray-300"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                data-testid="dont-show-again-checkbox"
              />
              <label htmlFor="dontShowAgain" className="text-sm text-muted-foreground cursor-pointer">
                Don't show this message again
              </label>
            </div>
            <Button
              className="w-full"
              onClick={dismissRefreshWarning}
              data-testid="dismiss-refresh-warning-btn"
            >
              Got it, I'll save now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* ── end refresh warning popup ── */}

    </DashboardLayout>
  );
}
